import { LANGUAGE_CN } from '@home/_config/config';
import { Relation } from '@intelligence/components/GenreSelect/enums';
import { GENRE_SOURCE_MAP, RELATION_JOINERS } from '@intelligence/components/GenreSelect/constants';

import type { MainGenre, SubGenre, IGenreSelectorPayload } from '@intelligence/components/GenreSelectMiddleware/types';
import type {
  ITag,
  OldSubGenre,
  OldMainGenre,
  OldPayload,
  NewPayload,
} from '@intelligence/components/GenreSelectMiddleware/Supports/types';

export const getGenreReqParamsForOld = (payload: IGenreSelectorPayload): OldPayload => {
  if (!payload)
    return {
      source: GENRE_SOURCE_MAP.APP_ANNIE,
      mainGenres: [],
      subGenres: [],
    };
  const { source, mainGenres, subGenres } = payload;
  let _mainGenres: OldMainGenre[] = [];
  let _subGenres: OldSubGenre[] = [];
  if (Array.isArray(mainGenres)) {
    _mainGenres = mainGenres.map((item) => item.value);
  }
  if (Array.isArray(subGenres)) {
    _subGenres = subGenres.map((item) => ({ main: item.parentValue, sub: item.value }));
  }
  return {
    source,
    mainGenres: _mainGenres,
    subGenres: _subGenres,
  };
};

export const getGenreReqParamsForNew = (payload: IGenreSelectorPayload): NewPayload => {
  const { source, mainGenres, subGenres, level3Genres, level4Genres, level5Genres, options } = payload || {};

  if (!Array.isArray(mainGenres) || mainGenres.length === 0) {
    return {
      source: source || GENRE_SOURCE_MAP.IEGG,
      genres: [],
    };
  }

  const genres = mainGenres.map((item) => {
    const children = [];
    // 使用新的级联数据结构查找主品类option
    const mainGenreOption = options?.find((option) => option?.value === item?.value);

    // 处理第2层品类（subGenres）
    for (let i = 0; i < (subGenres?.length || 0); i++) {
      const subGenre = subGenres[i];
      if (!subGenre) {
        continue;
      }
      if (subGenre.parentValue === item.value) {
        const level3Children = [];
        // 查找第2层品类的option，用于判断第3层是否全选
        const subGenreOption = mainGenreOption?.children?.find((option) => option?.value === subGenre.value);

        // 处理第3层品类（level3Genres）
        if (level3Genres && level3Genres.length > 0) {
          for (let j = 0; j < level3Genres.length; j++) {
            const level3Genre = level3Genres[j];
            if (!level3Genre) {
              continue;
            }
            if (level3Genre.parentValue === item.value && level3Genre.parent2Value === subGenre.value) {
              const level4Children = [];
              // 查找第3层品类的option，用于判断第4层是否全选
              const level3GenreOption = (subGenreOption as any)?.children?.find(
                (option: any) => option?.value === level3Genre.value,
              );

              // 处理第4层品类（level4Genres），并嵌套第5层品类（level5Genres）
              if (level4Genres && level4Genres.length > 0) {
                for (let k = 0; k < level4Genres.length; k++) {
                  const level4Genre = level4Genres[k];
                  if (!level4Genre) {
                    continue;
                  }
                  if (
                    level4Genre.parentValue === item.value &&
                    level4Genre.parent2Value === subGenre.value &&
                    level4Genre.parent3Value === level3Genre.value
                  ) {
                    // 查找第4层品类的option，用于判断第5层是否全选
                    const level4GenreOption = (level3GenreOption as any)?.children?.find(
                      (option: any) => option?.value === level4Genre.value,
                    );

                    // 处理第5层品类（level5Genres）
                    const level5Children: any[] = [];
                    if (level5Genres && level5Genres.length > 0) {
                      for (let m = 0; m < level5Genres.length; m++) {
                        const level5Genre = level5Genres[m];
                        if (!level5Genre) {
                          continue;
                        }
                        if (
                          level5Genre.parentValue === item.value &&
                          level5Genre.parent2Value === subGenre.value &&
                          level5Genre.parent3Value === level3Genre.value &&
                          level5Genre.parent4Value === level4Genre.value
                        ) {
                          level5Children.push({ value: level5Genre.value });
                        }
                      }
                    }

                    // 第5层全选判断：当前第4层品类下的第5层品类全部勾选的话，children字段要传空数组给后端
                    let level5ChildrenResult: any[] | null = null;
                    const level5GenreOptionsLength = level4GenreOption?.children?.length || 0;

                    if (level5GenreOptionsLength > 0) {
                      if (level5GenreOptionsLength === level5Children?.length) {
                        level5ChildrenResult = [];
                      } else if (level5Children.length > 0) {
                        level5ChildrenResult = level5Children;
                      } else {
                        level5ChildrenResult = [];
                      }
                    }

                    level4Children.push({
                      value: level4Genre.value,
                      ...(level5ChildrenResult === null ? {} : { children: level5ChildrenResult }),
                    });
                  }
                }
              }

              // 第4层全选判断：当前第3层品类下的第4层品类全部勾选的话，children字段要传空数组给后端
              let level4ChildrenResult: any[] | null = null;
              const level4GenreOptionsLength = level3GenreOption?.children?.length || 0;

              if (level4GenreOptionsLength > 0) {
                if (level4GenreOptionsLength === level4Children?.length) {
                  level4ChildrenResult = [];
                } else if (level4Children.length > 0) {
                  level4ChildrenResult = level4Children;
                } else {
                  level4ChildrenResult = [];
                }
              }

              level3Children.push({
                value: level3Genre.value,
                ...(level4ChildrenResult !== null ? { children: level4ChildrenResult } : {}),
              });
            }
          }
        }

        // 第3层全选判断：当前第2层品类下的第3层品类全部勾选的话，children字段要传空数组给后端
        let level3ChildrenResult: any[] | null = null;
        const level3GenreOptionsLength = (subGenreOption as any)?.children?.length || 0;

        if (level3GenreOptionsLength > 0) {
          if (level3GenreOptionsLength === level3Children?.length) {
            level3ChildrenResult = [];
          } else if (level3Children.length > 0) {
            level3ChildrenResult = level3Children;
          } else {
            level3ChildrenResult = [];
          }
        }

        children.push({
          value: subGenre.value,
          ...(level3ChildrenResult !== null ? { children: level3ChildrenResult } : {}),
        });
      }
    }

    return {
      value: item.value,
      relation: item.relation,
      // 第2层全选判断：当前主品类下的副品类全部勾选的话，children字段要传空数组给后端
      children: mainGenreOption?.children?.length === children?.length ? [] : children,
    };
  });

  return {
    source,
    // @ts-ignore
    genres,
  };
};

export const getGenresReqParams = (params: any, payload: IGenreSelectorPayload) => {
  const _params = { ...(params || {}) };
  let genresParams;
  if (payload.source === GENRE_SOURCE_MAP.IEGG) {
    genresParams = getGenreReqParamsForNew(payload);
    genresParams = {
      genre_source: genresParams?.source || '',
      iegg_genre_req: genresParams?.genres || [],
    };
    delete _params.genre;
    delete _params.sub_genre;
  } else {
    genresParams = getGenreReqParamsForOld(payload);
    genresParams = {
      genre: genresParams?.mainGenres || [],
      sub_genre: genresParams?.subGenres || [],
    };
    delete _params.genre_source;
    delete _params.iegg_genre_req;
  }
  return params ? { ..._params, ...genresParams } : genresParams;
};

export const getSelectedStr = (data: ITag[]): string => {
  if (!Array.isArray(data)) return '';
  return data.map((item) => item.label).join(', ');
};

export const getSelectedData = (mainGenres: MainGenre[], subGenres: SubGenre[]): ITag[] => {
  const result: ITag[] = [];
  if (!Array.isArray(mainGenres) || !Array.isArray(subGenres)) return result;

  for (let i = 0; i < mainGenres?.length; i++) {
    const main = mainGenres[i];
    if (!main?.value) continue;
    const relation = (main.relation as unknown as Relation) || Relation.Or;
    const relationJoiner = RELATION_JOINERS[relation] || RELATION_JOINERS[Relation.Or];
    const subs = [];
    let label = '';

    for (let j = 0; j < subGenres?.length; j++) {
      const sub = subGenres[j];
      if (!sub?.value || !sub?.parentValue) continue;
      if (sub.parentValue === main.value) {
        subs.push({ label: sub.name, value: sub.value });
      }
    }

    label = subs.map((item) => item.label || item.value).join(relationJoiner);
    result.push({
      label: `${main.name}: ${label}`,
      value: main.value,
    });
  }
  return result;
};

export const renderGenreBotton = (locale: string, selectedMainGenres: any[], selectedSubGenres: any[]) => {
  const isCn = locale === LANGUAGE_CN;

  const mainGenresNew = selectedMainGenres?.map((item: any) => {
    return {
      ...item,
      name: isCn ? item?.textCn : item?.textEn,
    };
  });
  const subGenresNew = selectedSubGenres?.map((item: any) => {
    return {
      ...item,
      name: isCn ? item?.textCn : item?.textEn,
    };
  });

  const result = getSelectedStr(getSelectedData(mainGenresNew, subGenresNew));

  return result;
};
